import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogOffering, CatalogService, CatalogWarehouse, InventoryReplenishmentPolicy } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-replenishment-policy-detail',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, FormsModule, ButtonModule, CheckboxModule, InputTextModule, AinCanAccessDirective],
  template: `
    <ain-page [title]="t('catalog.replenishment.detailTitle')" [description]="offeringLabel(policy()?.offeringId || '')">
      <ain-inventory-page-hint pageKey="replenishmentPolicyDetail" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!policy()) {
        <ain-state type="empty" [title]="t('catalog.replenishment.notFound')" />
      } @else {
        <section class="head">
          <a [routerLink]="['/inventory/replenishment-policies']">{{ t('catalog.action.backToList') }}</a>
        </section>
        <div class="form-grid">
          <label><span>{{ t('catalog.replenishment.minimum') }}</span><input pInputText type="number" [(ngModel)]="form.minimumQuantity" /></label>
          <label><span>{{ t('catalog.replenishment.reorderPoint') }}</span><input pInputText type="number" [(ngModel)]="form.reorderPoint" /></label>
          <label><span>{{ t('catalog.replenishment.maximum') }}</span><input pInputText type="number" [(ngModel)]="form.maximumQuantity" /></label>
          <label><span>{{ t('catalog.replenishment.safetyStock') }}</span><input pInputText type="number" [(ngModel)]="form.safetyStock" /></label>
          <label><span>{{ t('catalog.replenishment.leadTimeDays') }}</span><input pInputText type="number" [(ngModel)]="form.leadTimeDays" /></label>
          <label class="check"><p-checkbox [binary]="true" [(ngModel)]="form.isActive" /><span>{{ t('catalog.replenishment.active') }}</span></label>
        </div>
        <button pButton type="button" ainCanAccess [ainCanAccessPermission]="'inventory.replenishment.manage'" [label]="t('catalog.action.save')" (click)="save()"></button>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .head { margin-bottom: 12px; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-bottom: 12px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.check { display: flex; align-items: center; gap: 8px; }
    .error { color: #b91c1c; }
  `]
})
export class InventoryReplenishmentPolicyDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly localization = inject(LocalizationService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly policy = signal<InventoryReplenishmentPolicy | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  form = { minimumQuantity: 0, reorderPoint: 0, maximumQuantity: 0, safetyStock: 0, leadTimeDays: 0, isActive: true };

  t(key: string): string { return this.localization.translate(key); }
  offeringLabel(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : id;
  }

  ngOnInit(): void {
    this.service.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.service.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => void this.load());
  }

  save(): void {
    const id = this.policy()?.id;
    if (!id) return;
    this.service.updateInventoryReplenishmentPolicy(id, this.form).subscribe({
      next: p => { this.policy.set(p); this.syncForm(p); },
      error: () => this.error.set(this.t('catalog.replenishment.saveFailed'))
    });
  }

  private load(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;
    this.loading.set(true);
    this.service.getInventoryReplenishmentPolicy(id).subscribe({
      next: p => { this.policy.set(p); this.syncForm(p); this.loading.set(false); },
      error: () => { this.policy.set(null); this.loading.set(false); }
    });
  }

  private syncForm(p: InventoryReplenishmentPolicy): void {
    this.form = {
      minimumQuantity: p.minimumQuantity,
      reorderPoint: p.reorderPoint,
      maximumQuantity: p.maximumQuantity,
      safetyStock: p.safetyStock,
      leadTimeDays: p.leadTimeDays,
      isActive: p.isActive
    };
  }
}
